import { useFormikContext } from 'formik'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { SearchCancelButtons } from 'src/views/components/forms/formik/SearchCancelButtons'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import { GRID_THREE } from '../../programs/AdministratorProgramPage/constants'
import {
  DEFAULT_PER_PAGE,
  formValuesToSearchParams,
  GRADUATES_TYPES,
  parseFormValues,
  type GraduatesFormValues,
} from './query'

// Backend maximum
const PROGRAMS_PER_PAGE = 100

export default function GraduatesFilter(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSemester = useActiveSemester()

  const initialValues = React.useMemo((): GraduatesFormValues => {
    const values = parseFormValues(searchParams)
    return { ...values, semId: values.semId || (activeSemester?.id ?? '') }
  }, [searchParams, activeSemester?.id])

  const handleSubmit = React.useCallback(
    (values: GraduatesFormValues) => {
      setSearchParams(formValuesToSearchParams(values, searchParams.get('perPage') ?? DEFAULT_PER_PAGE))
    },
    [searchParams, setSearchParams]
  )

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      classNames={{ form: 'w-full mt-3 mb-1' }}
      enableReinitialize
    >
      <FilterFields />
    </Form>
  )
}

function FilterFields(): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<GraduatesFormValues>()
  const [searchParams, setSearchParams] = useSearchParams()
  const headers = useAuthenticatedHeaders()
  const { facultyId, degree, type } = formik.values

  const { data: semesters, isLoading: semestersLoading } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const { data: degrees, isLoading: degreesLoading } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const { data: faculties, isLoading: facultiesLoading } = useApi({
    endpoint: Api.getAdministratorListsFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const { data: statuses, isLoading: statusesLoading } = useApi({
    endpoint: Api.getAdministratorListsStudentStatuses,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const { data: schools, isLoading: schoolsLoading } = useApi({
    endpoint: Api.getAdministratorListsSchools,
    params: React.useMemo(
      () => ({ headers, query: { ...(facultyId !== '' && { facultyId: parseInt(facultyId, 10) }) } }),
      [headers, facultyId]
    ),
    suspense: false,
  })
  // lists/programs returns codes, the backend needs program ids
  const { data: programs, isLoading: programsLoading } = useApi({
    endpoint: Api.getAdministratorPrograms,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          perPage: PROGRAMS_PER_PAGE,
          ...(facultyId !== '' && { facultyId: parseInt(facultyId, 10) }),
          ...(degree !== '' && { degree: parseInt(degree, 10) }),
        },
      }),
      [headers, facultyId, degree]
    ),
    suspense: false,
  })

  // School and program lists depend on faculty/degree, so a stale selection is cleared when those change
  const previous = React.useRef({ facultyId, degree })
  React.useEffect(() => {
    const facultyChanged = previous.current.facultyId !== facultyId
    const degreeChanged = previous.current.degree !== degree
    previous.current = { facultyId, degree }
    // Reinitialized from the URL (search, cancel, back/forward): the URL values are already consistent
    if (facultyId === formik.initialValues.facultyId && degree === formik.initialValues.degree) return
    if (facultyChanged) {
      void formik.setFieldValue('schoolId', '')
    }
    if (facultyChanged || degreeChanged) {
      void formik.setFieldValue('programId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facultyId, degree])

  const anyOption = React.useMemo(() => ({ value: '', label: t('common:any') }), [t])

  const typeOptions = React.useMemo(
    () => GRADUATES_TYPES.map((option) => ({ value: option.value, label: t(option.label) })),
    [t]
  )
  const typeDescription = GRADUATES_TYPES.find((option) => option.value === type)?.description

  // A future semester is rejected by the backend (422)
  const semesterOptions = React.useMemo(
    () =>
      (semesters ?? [])
        .filter((semester) => semester.isFuture !== true)
        .map((semester) => ({
          value: semester.id,
          label: semester.name + (semester.isActive ? t('common:current') : ''),
        })),
    [semesters, t]
  )
  const degreeOptions = React.useMemo(
    () => [anyOption, ...(degrees ?? []).map((item) => ({ value: String(item.id), label: item.name }))],
    [degrees, anyOption]
  )
  const facultyOptions = React.useMemo(
    () => [anyOption, ...(faculties?.data ?? []).map((item) => ({ value: String(item.id), label: item.name }))],
    [faculties, anyOption]
  )
  const statusOptions = React.useMemo(
    () => (statuses ?? []).map((item) => ({ value: item.id, label: item.name })),
    [statuses]
  )
  const schoolOptions = React.useMemo(
    () => [anyOption, ...(schools ?? []).map((item) => ({ value: item.id, label: item.name }))],
    [schools, anyOption]
  )
  const programOptions = React.useMemo(
    () => [anyOption, ...(programs?.data ?? []).map((item) => ({ value: item.id, label: item.name }))],
    [programs, anyOption]
  )

  const isFresh = !searchParams.has('type')

  const handleCancel = React.useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col gap-4">
      <FormError />

      <SelectInput
        label={t('statistics:statistics_type')}
        name="type"
        options={typeOptions}
        instructions={
          typeDescription != null ? <span className="text-sm text-captionColor">{t(typeDescription)}</span> : undefined
        }
      />

      <div className={GRID_THREE}>
        <SelectInput label={t('common:semester')} name="semId" options={semesterOptions} isLoading={semestersLoading} />
        <TextInput type="text" label={t('common:search')} name="q" placeholder={t('statistics:search_placeholder')} />
        <SelectInput
          label={t('common:status')}
          name="eduStatuses"
          options={statusOptions}
          isLoading={statusesLoading}
          isMulti
          storeValueOnly
          closeMenuOnSelect={false}
        />
        <SelectInput label={t('person:degree')} name="degree" options={degreeOptions} isLoading={degreesLoading} />
        <SelectInput
          label={t('faculty:faculty')}
          name="facultyId"
          options={facultyOptions}
          isLoading={facultiesLoading}
        />
        <SelectInput
          label={t('statistics:minor_faculty')}
          name="minorFacultyId"
          options={facultyOptions}
          isLoading={facultiesLoading}
        />
        <SelectInput
          label={t('faculty:faculty_school')}
          name="schoolId"
          options={schoolOptions}
          isLoading={schoolsLoading}
        />
        <SelectInput
          label={t('program:programme')}
          name="programId"
          options={programOptions}
          isLoading={programsLoading}
        />
      </div>

      <SearchCancelButtons isDirty={formik.dirty} canCancel={isFresh} onCancel={handleCancel} />
    </fieldset>
  )
}
